import { useState, useEffect } from "react";
import { X, ChevronRight, ChevronLeft, Sparkles, Presentation, History, Trophy, SkipForward } from "lucide-react";
import { Button } from "./ui/button";

const tourSteps = [
  {
    id: "welcome",
    title: "Welcome to EVOLVE! 🎉",
    description: "Let's take a quick tour to help you get started with improving your presentation skills.",
    icon: Sparkles,
    target: null, // No specific target for welcome
    position: "center"
  },
  {
    id: "new-session",
    title: "Start a New Session",
    description: "Click 'New Session' to create a practice presentation. Upload your slides, present to the AI, and get instant feedback!",
    icon: Presentation,
    target: "[data-testid='new-session-btn']",
    position: "right"
  },
  {
    id: "my-sessions",
    title: "View Your Sessions",
    description: "Check 'My Sessions' to see all your past presentations, scores, and detailed feedback. Track your improvement over time!",
    icon: History,
    target: "[data-testid='nav--dashboard']",
    position: "right"
  },
  {
    id: "leaderboard",
    title: "Check the Leaderboard",
    description: "See how you rank against your colleagues! The leaderboard shows top performers and motivates continuous improvement.",
    icon: Trophy,
    target: "[data-testid='nav--leaderboard']",
    position: "right"
  }
];

export default function OnboardingTour({ onComplete, forceShow = false }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [targetRect, setTargetRect] = useState(null);

  useEffect(() => {
    // Check if user has seen tour today or if force show is enabled
    if (forceShow) {
      setIsVisible(true);
      setCurrentStep(0);
      return;
    }
    
    const lastTourDate = localStorage.getItem("evolve_tour_date");
    const today = new Date().toDateString();
    
    if (lastTourDate !== today) {
      setIsVisible(true);
    }
  }, [forceShow]);

  useEffect(() => {
    if (!isVisible) return;
    
    const step = tourSteps[currentStep];
    if (step.target) {
      // Find and highlight the target element
      const targetElement = document.querySelector(step.target);
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        setTargetRect(rect);
        
        // Scroll element into view if needed
        targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        setTargetRect(null);
      }
    } else {
      setTargetRect(null);
    }
  }, [currentStep, isVisible]);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    completeTour();
  };

  const completeTour = () => {
    // Mark tour as completed for today
    localStorage.setItem("evolve_tour_date", new Date().toDateString());
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  if (!isVisible) return null;

  const step = tourSteps[currentStep];
  const Icon = step.icon;
  const isLastStep = currentStep === tourSteps.length - 1;
  const isFirstStep = currentStep === 0;

  // Calculate tooltip position - ensure it stays within viewport
  const getTooltipStyle = () => {
    if (!targetRect || step.position === "center") {
      return {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)"
      };
    }

    const padding = 20;
    const tooltipWidth = 360;
    const tooltipHeight = 280; // Approximate height
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Calculate best position
    let top = targetRect.top;
    let left = targetRect.right + padding;

    // If tooltip would go off right edge, position to left of target
    if (left + tooltipWidth > viewportWidth - padding) {
      left = Math.max(padding, targetRect.left - tooltipWidth - padding);
    }

    // If tooltip would go off bottom, adjust top
    if (top + tooltipHeight > viewportHeight - padding) {
      top = Math.max(padding, viewportHeight - tooltipHeight - padding);
    }

    // Ensure top is not negative
    top = Math.max(padding, top);

    return {
      position: "fixed",
      top: top,
      left: left
    };
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/60 z-[100] transition-opacity duration-300"
        onClick={handleSkip}
      />
      
      {/* Highlight ring around target */}
      {targetRect && (
        <div
          className="fixed z-[101] pointer-events-none"
          style={{
            top: targetRect.top - 6,
            left: targetRect.left - 6,
            width: targetRect.width + 12,
            height: targetRect.height + 12,
            border: "3px solid #14B8A6",
            borderRadius: "12px",
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.5), 0 0 20px rgba(20,184,166,0.5)",
            animation: "pulse 2s ease-in-out infinite"
          }}
        />
      )}
      
      {/* Tooltip Card */}
      <div 
        className="z-[102] w-[360px] bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-in"
        style={getTooltipStyle()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-accent p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white/70 text-xs font-medium">
                  Step {currentStep + 1} of {tourSteps.length}
                </p>
                <h3 className="text-white font-bold text-lg" style={{ fontFamily: 'Manrope' }}>
                  {step.title}
                </h3>
              </div>
            </div>
            <button 
              onClick={handleSkip}
              className="text-white/70 hover:text-white transition-colors p-1"
              aria-label="Close tour"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-5">
          <p className="text-[#5C5C5C] leading-relaxed">
            {step.description}
          </p>
        </div>
        
        {/* Progress dots */}
        <div className="flex justify-center gap-2 pb-3">
          {tourSteps.map((_, idx) => (
            <div
              key={idx}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentStep 
                  ? 'bg-primary w-6' 
                  : idx < currentStep 
                    ? 'bg-primary/50' 
                    : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-[#F9FAFB]">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSkip}
            className="text-[#5C5C5C] hover:text-[#1A1A1A]"
          >
            <SkipForward className="w-4 h-4 mr-1" />
            Skip Tour
          </Button>
          
          <div className="flex gap-2">
            {!isFirstStep && (
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrev}
                className="rounded-full"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleNext}
              className="rounded-full bg-primary hover:bg-primary/90"
            >
              {isLastStep ? (
                <>
                  Get Started
                  <Sparkles className="w-4 h-4 ml-1" />
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 0 0 9999px rgba(0,0,0,0.5), 0 0 20px rgba(20,184,166,0.3);
          }
          50% {
            box-shadow: 0 0 0 9999px rgba(0,0,0,0.5), 0 0 30px rgba(20,184,166,0.6);
          }
        }
      `}</style>
    </>
  );
}
